import { ReactElement } from 'react';
import { DetailsPanel, FormatInformation, Typography } from '@weareopenr/component-library';
import styles from './ProductRecipeTab.module.scss';

import { formatDate } from 'src/helpers/formatDate/formatDate';
import { useItem } from 'src/hooks/useItem/useItem';
import { ClaimsCode } from 'src/constants';
import { Link } from 'react-router-dom';
import { IAllergen, IClaims } from 'src/types/menu.types';
import { Routes } from 'src/routes';

/**
 * Extracts unique claims from an array of claims
 * @param {IClaims[]} claims - Array of claim objects to convert into a string
 * @return {string | null} - A string of unique claims or null if no claims are found
 * * */
export const getClaims = (claims: IClaims[]): string | null => {
  const filteredClaims = claims.filter((claim) => claim.code !== ClaimsCode.ALCOHOL);
  const uniqueClaims = Array.from(new Set(filteredClaims.map((c) => c.name)));

  /**
   * We're only concerned about displaying vegan/vegetarian as a category.
   * The 'Contains alcohol' claim is displayed elsewhere in the product details panel, and
   * can be omitted from this list.
   */
  return uniqueClaims.length ? uniqueClaims.join(', ') : null;
};

/**
 * @description Convert an array of allergens into a comma-separated string
 * @param {IAllergen[]} allergens - Array of allergen objects
 * @returns {string | null} - Comma-separated string of unique allergens or null if input is invalid/empty
 */
export const getAllergens = (allergens: IAllergen[]): string | null => {
  if (!Array.isArray(allergens)) {
    return null;
  }

  if (allergens.length === 0) {
    return null;
  }

  return Array.from(new Set(allergens.map((a) => a.name))).join(', ');
};

const ProductRecipeTab = ({ itemId }: { itemId: string }): ReactElement => {
  const { data: menuItem } = useItem(itemId);
  const recipe = menuItem.recipe;

  const calories = recipe.calories ?? 'No Calorie Information Provided';
  const containsAlcohol = recipe.claims.some((claim) => claim.code === ClaimsCode.ALCOHOL) ? 'Yes' : 'No';
  return (
    <div className={styles['container']}>
      <div className={styles['header']}>
        <Typography size="xlarge" weight="semi-bold">
          Recipe Details
        </Typography>

        <Typography colour="primary" className={styles['timestamp']}>
          {`Last updated at ${formatDate(new Date(recipe?.modification_date))}`}
        </Typography>
      </div>

      <DetailsPanel>
        <DetailsPanel.Row>
          <DetailsPanel.Column>
            <FormatInformation heading="Name">
              <Link to={`${Routes.INVENTORY}/${recipe.product_id}`}>
                <Typography colour="primary">{recipe?.name}</Typography>
              </Link>
            </FormatInformation>
            {recipe?.code && (
              <FormatInformation heading="Recipe Code">
                <Typography colour="primary">{recipe?.code}</Typography>
              </FormatInformation>
            )}
            <FormatInformation heading="Calories">{calories}</FormatInformation>
            <FormatInformation heading="Contains Alcohol">{containsAlcohol}</FormatInformation>
            <FormatInformation heading="Allergens">
              {getAllergens(recipe.allergens?.filter((allergen) => allergen.type === 'KNOWN')) || 'None'}
            </FormatInformation>
            <FormatInformation heading="May Contain">
              {getAllergens(recipe.allergens?.filter((allergen) => allergen.type === 'POTENTIAL')) || 'None'}
            </FormatInformation>
            <FormatInformation heading="Claims">{getClaims(recipe.claims) || 'No Claims'} </FormatInformation>
            {recipe.ingredients && <FormatInformation heading="Ingredients">{recipe.ingredients} </FormatInformation>}
            {recipe.ingredient_declaration && (
              <FormatInformation heading="Ingredient Declaration">{recipe.ingredient_declaration} </FormatInformation>
            )}
          </DetailsPanel.Column>
        </DetailsPanel.Row>
      </DetailsPanel>
    </div>
  );
};

export default ProductRecipeTab;
